/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { queryClient } from 'lib/react-query'
import { InfiniteData } from 'react-query'
import { InfinityNewType } from 'types'
import { baseTime } from 'lib'
import {
 ChatDateGroupMessage,
 TChatListData,
 TDirectChat,
 TChatHistoryList,
 TGetChatMessage,
} from './types'
import { TUnreadCountResp } from './hooks/usePusherUnread'

const PER_PAGE = 50

export const playChatAudio = async () => {
 const audio = new Audio('/audio/chat.mp3')
 audio.autoplay = true
 const promise = audio.play()
 if (promise !== undefined) {
  promise
   .then(() => {
    // Autoplay started
   })
   .catch(() => {
    // Autoplay was prevented.
    audio.muted = true
    audio.play()
   })
 }
}

export const goToChatBottom = () => {
 const msgArea = document.getElementById('lastmessage')
 if (msgArea) {
  msgArea.scrollIntoView({ behavior: 'auto', block: 'end', inline: 'end' })
 }
}

export function moveValueToFront(arr: TChatListData[], val: TChatListData) {
 const index = arr.findIndex((v) => v.leadId === val.leadId)
 if (index !== -1) {
  arr.splice(index, 1)
  arr.unshift(val)
 }
 return arr
}

const dateFormat = (date: string) => {
 if (baseTime(date).isSame(new Date(), 'day')) {
  return 'Today'
 }
 if (baseTime(date).isSame(baseTime(new Date()).subtract(1, 'day'), 'day')) {
  return 'Yesterday'
 }
 return baseTime(date).format('ddd, DD MMM')
}

export const formatChatTime = (dateStr?: string) => {
 if (!dateStr) return ''
 const date = baseTime(dateStr)
 const now = baseTime()

 if (date.isSame(now, 'day')) {
  return date.format('h:mm A')
 }

 if (date.isSame(now.clone().subtract(1, 'days'), 'day')) {
  return 'Yesterday'
 }

 if (date.isAfter(now.clone().subtract(7, 'days'), 'day')) {
  return date.format('dddd')
 }

 return date.format('DD/MM/YYYY')
}

export const groupMsgByDate = (list?: TGetChatMessage[]) => {
 if (!list || (list && list.length === 0)) return []
 const msgList: ChatDateGroupMessage[] = []

 list.forEach((item) => {
  if (item.created_at) {
   const date = dateFormat(item.created_at)
   const index = msgList.findIndex((group) => group.date === date)
   if (index !== -1) {
    msgList[index].messages?.unshift(item)
   } else {
    const newGroup = { date, messages: [item] }
    msgList.push(newGroup)
   }
  }
 })

 return msgList
}

function updateMsgListCache(messages?: (TChatHistoryList | undefined)[]) {
 queryClient.setQueryData<InfiniteData<InfinityNewType<TChatHistoryList>>>(
  ['chat/list', '', ''],
  (data) => {
   return {
    pageParams: data?.pageParams || [],
    pages: [
     {
      data: (messages as TChatHistoryList[]) || [],
      next: 2,
     },
    ] as InfinityNewType<TChatHistoryList>[],
   }
  },
 )
}

function updateDirectMsgCache(
 senderId?: number,
 messages?: (TDirectChat | undefined)[],
) {
 queryClient.setQueryData<InfiniteData<InfinityNewType<TDirectChat>>>(
  `messages/${senderId}`,
  (data) => {
   return {
    pageParams: data?.pageParams || [],
    pages: [
     {
      data: messages?.slice(0, 0 + PER_PAGE),
      next: 2,
     },
    ] as InfinityNewType<TDirectChat>[],
   }
  },
 )
}

function updateChatMessageCacheData(leadId: any, messages: TGetChatMessage[]) {
 const currentPage: InfinityNewType<TGetChatMessage>[] = [
  {
   data: messages.slice(0, 0 + PER_PAGE),
   next: 2,
  },
 ]

 queryClient.setQueryData<InfiniteData<InfinityNewType<TGetChatMessage>>>(
  `chat-messages-${leadId}`,
  (data) => ({
   pageParams: data?.pageParams || [],
   pages: currentPage,
  }),
 )
}

type THandleDirectMsg = {
 type: 'add' | 'removeTemp' | 'dlt' | 'update'
 data: {
  data: TDirectChat
  sender_id: number
 }
 tempId?: string
}

export const handleDirectMsgToCache = (val: THandleDirectMsg) => {
 const cache = queryClient.getQueryData<
  InfiniteData<InfinityNewType<TDirectChat>>
 >(`messages/${val?.data?.sender_id}`)
 if (cache?.pages && cache?.pages.length > 0) {
  const msg = val?.data?.data
  const messages = cache?.pages.map((v) => v.data).flat() || []
  if (val?.type === 'add') {
   if (msg?.temp_id || !messages.find((v) => v?.id === msg?.id)) {
    const upMessages = messages
    upMessages.unshift(msg)
    updateDirectMsgCache(val?.data?.sender_id, upMessages)
   }
  } else if (val?.type === 'dlt') {
   const upMessages = messages.filter((v) => v?.id !== msg?.id)
   updateDirectMsgCache(val?.data?.sender_id, upMessages)
  } else if (val?.type === 'removeTemp') {
   const upMessages = messages.filter((v) => v?.temp_id !== val?.tempId)

   updateDirectMsgCache(val?.data?.sender_id, upMessages)
  } else if (val?.type === 'update') {
   const index = messages.findIndex((v) => v?.temp_id === val?.tempId)
   const upMessages = messages
   upMessages[index] = msg
   updateDirectMsgCache(val?.data?.sender_id, upMessages)
  }
 }
}

export const updateChatListCache = (data: TUnreadCountResp): void => {
 const cache = queryClient.getQueryData<
  InfiniteData<InfinityNewType<TChatHistoryList>>
 >(['chat/list', '', ''])
 if (cache?.pages && cache?.pages.length > 0 && data?.chat_id) {
  const messages =
   (cache?.pages.map((v) => v.data).flat() as TChatHistoryList[]) || []
  if (messages.find((v) => v?.id === data?.chat_id)) {
   const msgIndex = messages.findIndex((v) => v?.id === data?.chat_id)
   if (msgIndex !== -1) {
    const msg = { ...messages[msgIndex] } as TChatHistoryList
    const upMess = messages.filter((v) => v?.id !== data?.chat_id)
    msg.unreadCount = data?.unread_chat_count
    msg.latest_message_time = data?.last_message_date
    msg.latest_message = data?.last_message
    upMess.unshift(msg)
    updateMsgListCache(upMess)
   }
  }
 }
}

export const addChatMessageToCache = (leadId: any, msg: TGetChatMessage) => {
 const cache = queryClient.getQueryData<
  InfiniteData<InfinityNewType<TGetChatMessage>>
 >(`chat-messages-${leadId}`)
 if (cache?.pages && cache?.pages.length > 0) {
  const messages = cache?.pages.map((v) => v.data).flat() || []
  if (msg.tempId) {
   messages.unshift(msg)
  } else if (!messages.find((v) => v?.id === msg.id)) {
   messages.unshift(msg)
  }

  updateChatMessageCacheData(leadId, messages as any)
 } else {
  updateChatMessageCacheData(leadId, [msg])
 }
}

export const updateChatMessageToCache = (
 leadId: any,
 tempId: any,
 msg: TGetChatMessage,
) => {
 const cache = queryClient.getQueryData<
  InfiniteData<InfinityNewType<TGetChatMessage>>
 >(`chat-messages-${leadId}`)
 if (cache?.pages && cache?.pages.length > 0) {
  const messages =
   (cache?.pages.map((v) => v.data).flat() as TGetChatMessage[]) || []

  const index = messages.findIndex((v) => v.tempId === tempId)
  if (index !== -1) {
   messages[index] = msg
   updateChatMessageCacheData(leadId, messages)
  }
 }
}

export const removeTempChatMessageFromCache = (leadId: any, tempId: any) => {
 const cache = queryClient.getQueryData<
  InfiniteData<InfinityNewType<TGetChatMessage>>
 >(`chat-messages-${leadId}`)
 if (cache?.pages && cache?.pages.length > 0) {
  const messages =
   (cache?.pages.map((v) => v.data).flat() as TGetChatMessage[]) || []
  updateChatMessageCacheData(
   leadId,
   messages.filter((v) => v.tempId !== tempId),
  )
 }
}

export const markChatAsReadInCache = (chatId: number) => {
 const cacheKey = ['chat/list', '', '']
 const cache =
  queryClient.getQueryData<InfiniteData<InfinityNewType<TChatHistoryList>>>(
   cacheKey,
  )

 if (cache?.pages && cache?.pages.length > 0) {
  const newPages = cache.pages.map((page) => ({
   ...page,
   data:
    page.data?.map((chat) => {
     if (chat.id === chatId) {
      return { ...chat, unreadCount: 0 }
     }
     return chat
    }) ?? [],
  }))

  queryClient.setQueryData<InfiniteData<InfinityNewType<TChatHistoryList>>>(
   cacheKey,
   {
    ...cache,
    pages: newPages,
   },
  )
 }
}
